import EventEmitter from 'events';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo } from 'react';
import { Badge } from 'react-bootstrap';

import { IconButton } from 'src/libs/ui-kit/buttons/IconButton/IconButton';
import { WidgetBase } from 'src/libs/ui-kit/widget-base/WidgetBase';

import {
  BlockingModal,
  ContractorSummary,
  CustomerSummary,
  EmployeeSummary,
  EditModal,
  RevocationModal,
  StoreProvider,
  SwitchContainer,
  UnblockingModal,
  useProfileWidgetStore,
} from './components';

import { IUserProfile, IUserProfileServiceProvider } from './interfaces';
import { TUserTypes } from './store';

interface IUserProfileWidgetProps {
  id: string;
  col?: number;
  profileService: IUserProfileServiceProvider;
  userType: TUserTypes;
  eventEmitter: EventEmitter;
  onProfileLoaded?(profile: IUserProfile): void;
  showRevocationButton?: boolean;
  badgeText?: string;
}

export const UserProfileWidget = observer((props: IUserProfileWidgetProps) => (
  <StoreProvider>
    <UserProfileWidgetView {...props} />
  </StoreProvider>
));

const UserProfileWidgetView = observer(
  ({
    id,
    col,
    profileService,
    onProfileLoaded,
    userType,
    eventEmitter,
    showRevocationButton,
    badgeText,
  }: IUserProfileWidgetProps) => {
    const {
      init,
      profile,
      showEditForm,
      blockingModal,
      isLoading,
      hasError,
      onUnblockUserClick,
      onRevocationPersonalDataUserClick,
    } = useProfileWidgetStore();

    useEffect(
      () =>
        init({
          userId: id,
          profileService,
          userType,
          eventEmitter,
        }).unload,
      [id, init, profileService, userType, eventEmitter],
    );

    useEffect(() => profile && onProfileLoaded && onProfileLoaded(profile), [onProfileLoaded, profile]);

    const isBlocked = Boolean(profile?.isBlocked);
    const isDeleted = Boolean(profile?.isDeleted);

    const buttons = useMemo(() => {
      const buttons =
        userType === 'customer' || userType === 'contractor'
          ? [{ render: () => <IconButton size="sm" icon="edit" clickHandler={showEditForm} /> }]
          : [];

      if (showRevocationButton) {
        const revocationButton = {
          render: () => {
            if (!isDeleted && isBlocked) {
              return <IconButton size="sm" icon="lock-open" clickHandler={onUnblockUserClick} />;
            }

            if (!isDeleted && !isBlocked) {
              return (
                <IconButton
                  size="sm"
                  disabled={isDeleted}
                  icon="user-xmark"
                  clickHandler={onRevocationPersonalDataUserClick}
                />
              );
            }
            return <IconButton size="sm" disabled icon="lock-open" clickHandler={onUnblockUserClick} />;
          },
        };

        buttons.push(revocationButton);
      }

      return buttons;
    }, [
      blockingModal.show,
      isBlocked,
      isDeleted,
      showRevocationButton,
      showEditForm,
      userType,
      onUnblockUserClick,
      onRevocationPersonalDataUserClick,
    ]);

    return (
      <>
        <WidgetBase
          title={
            <div className="d-flex align-items-center">
              <span>Профиль</span>
              {badgeText ? (
                <Badge variant="warning" className="ml-2">
                  {badgeText}
                </Badge>
              ) : null}
            </div>
          }
          col={col}
          buttons={buttons}
          isLoading={isLoading}
          hasError={hasError}>
          <div className="px-5">
            <SwitchContainer
              Customer={CustomerSummary}
              Contractor={ContractorSummary}
              Employee={EmployeeSummary}
              Partner={CustomerSummary}
            />
          </div>
        </WidgetBase>

        <BlockingModal />
        <UnblockingModal />
        <RevocationModal />
        <EditModal />
      </>
    );
  },
);
